import { Button } from '@teach/ui'
import { FailureBanner } from '@/features/ai/components/FailureBanner'
import { jobTypeLabel } from '@/features/ai/lib/jobTypeLabel'
import type { StartFailure } from '@/features/ai/lib/parseStartError'
import type { ImageQuoteView } from '@/features/ai/useImageQuote'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  quote: ImageQuoteView
  /** Все признаки считает диалог — теми же выражениями, что гасят запуск.
   *  Пересчитать их здесь значило бы завести вторую копию гейта. */
  hasStyle: boolean
  /** `styleSettled` диалога (`hasStyle || effectivePilotSets > 0`) — тот же
   *  булев, что уходит и в `canStartDraw`: при заказанном наборе пилотов
   *  отсутствие стиля перестаёт быть блокером, и баннер «без него отрисовка
   *  не запустится» стал бы ложью рядом с живой кнопкой «Нарисовать». Приходит
   *  уже готовым значением (Important 3, финальное ревью) — раньше сюда
   *  приезжал сырой `pilotSets`, и тело пересчитывало `pilotSets > 0` само,
   *  вопреки собственному докблоку про «вторую копию гейта». */
  styleSettled: boolean
  quoteFailed: boolean
  /** Курс на проверке — тот же признак, что уходит в `canStartDraw`. */
  isFrozen: boolean
  /** `useReserveReturning` курса — тоже из диалога: своего id у тела нет. */
  reserveReturning: boolean
  failure: StartFailure | null
  onOpenStyle: () => void
  /**
   * Повторный запуск отрисовки. Как и у соседних действий баннера, его
   * достижимость знает только родитель (тот же `canStart`, что гасит кнопку
   * «Нарисовать»). Без него ветка «кредиты предыдущего прогона ещё
   * возвращаются» осталась бы голым текстом: она предлагает подождать и
   * повторить, а повторять было нечем.
   */
  onRetry?: () => void
}

/**
 * Общие объяснения диалога отрисовки: почему запуск может быть погашен. Они
 * ОДНИ на оба режима — гейты старта общие, и вторая копия объяснения врала бы
 * ровно там, где первая молчит. План и цена живут отдельно: у авто-режима это
 * `AutoPlanSummary` (числа сервера), у ручного — подвал `TargetPicker`.
 */
export const DrawImagesBody = ({
  quote,
  hasStyle,
  styleSettled,
  quoteFailed,
  isFrozen,
  reserveReturning,
  failure,
  onOpenStyle,
  onRetry,
}: Props) => {
  const m = useMessages(messages)
  const busyJob = quote.activeJob
  return (
    <div className="space-y-3 text-sm">
      {/* Заказанный набор пилотов И ЕСТЬ выбор стиля — при урегулированном
          стиле отсутствие `image_style` больше не блокер (`canStartDraw`), и
          текст меняется на правдивый вместо того, чтобы молчать: экран без
          объяснения не говорил бы автору, почему у курса всё ещё нет стиля. */}
      {hasStyle ? null : styleSettled ? (
        <p className="text-xs text-muted-foreground">{m.drawStylePilot}</p>
      ) : (
        <p className="rounded-xl border border-warning/30 bg-warning/10 p-3 text-xs text-foreground">
          {m.drawStyleMissing}
        </p>
      )}
      {/* Молчаливое «К отрисовке: —» читалось бы как «нечего рисовать»: без
          сметы неизвестен ни план, ни цена, а при отказе GET /quote это
          состояние вечное — отсюда и повтор запроса. */}
      {quoteFailed ? (
        <div className="space-y-2 rounded-xl border border-warning/30 bg-warning/10 p-3 text-xs text-foreground">
          <p>{m.drawQuoteFailed}</p>
          <Button variant="outline" size="sm" onClick={quote.refetch}>
            {m.drawQuoteRefresh}
          </Button>
        </div>
      ) : null}
      {busyJob ? (
        <p className="text-xs text-muted-foreground">
          {busyJob.status === 'awaiting_author'
            ? // M-3 (финальное ревью): ответ запуск не завершает — граф
              // дорисовывает остаток, курс занят ещё минуты. «Отрисовку можно
              // запустить снова» обещало бы то, чего не будет.
              m.drawAwaitingAuthor(busyJob.job_id)
            : m.drawCourseBusy(busyJob.job_id, jobTypeLabel(busyJob.job_type))}
        </p>
      ) : null}
      {isFrozen ? <p className="text-xs text-muted-foreground">{m.drawFrozen}</p> : null}
      {failure ? (
        <FailureBanner
          failure={failure}
          onOpenStyle={onOpenStyle}
          onRetry={onRetry}
          reserveReturning={reserveReturning}
        />
      ) : null}
    </div>
  )
}
